import styled, { falconTheme, IFalconTheme, StyledComponent } from '@se-admin/falcon';
import * as React from 'react';
import { TranslationsContext } from '../../common/contexts';
import {
  HOURS_IN_DAY,
  MILLISECONDS_IN_SECOND,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from '../../common/types/constants';

import {
  getFullCountdownString,
  getSecondsLeft,
  getShortCountdownString,
  ITime,
  secondsToTime,
} from './Countdown.utils';

export interface ICountdownProps {
  color?: string;
  endDate: Date;
  variant?: CountdownVariant;
}

export interface ICountdownState {
  seconds: number;
}

export enum CountdownVariant {
  FULL,
  SHORT,
}

const CountdownStyle: StyledComponent<'span', IFalconTheme, Partial<ICountdownProps>> = styled(
  'span'
)`
  color: ${({ color, theme }) => (color ? color : theme.color.black)};
`;

CountdownStyle.defaultProps = {
  theme: falconTheme,
};

/**
 * A countdown that auto updates every second if there is less than one day remaining.
 * @param endDate The timer will count from endDate minus current time
 * @param color An optional parameter that changes the color of the countdown
 */
const Countdown: React.FunctionComponent<ICountdownProps> = props => {
  const [seconds, setSeconds] = React.useState(0);
  const [timer, setTimer] = React.useState(null);
  const { endDate } = props;

  React.useEffect(() => {
    const newSeconds: number = getSecondsLeft(endDate);
    setSeconds(newSeconds);

    if (newSeconds < SECONDS_IN_HOUR * HOURS_IN_DAY) {
      const secondsBetweenUpdates: number =
        props.variant === CountdownVariant.SHORT ? SECONDS_IN_MINUTE : 1;

      const newTimer: number = setInterval(
        countDown,
        secondsBetweenUpdates * MILLISECONDS_IN_SECOND
      );

      setTimer(newTimer);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, []);

  const { translations } = React.useContext(TranslationsContext);

  /**
   * Checks if the number of remaining seconds changed and update state if it does.
   * If the number of seconds gets to zero, the interval is cleared.
   */
  const countDown: () => void = React.useCallback(() => {
    const secondsLeft: number = getSecondsLeft(endDate);
    if (secondsLeft !== seconds) {
      setSeconds(secondsLeft);
    }

    if (secondsLeft === 0) {
      clearInterval(timer);
    }
  }, [seconds, timer]);
  const time: ITime = secondsToTime(seconds);

  let countdownString: string;
  if (props.variant === CountdownVariant.SHORT) {
    countdownString = getShortCountdownString(time, translations);
  } else {
    countdownString = getFullCountdownString(time, translations);
  }

  return <CountdownStyle color={props.color}>{countdownString}</CountdownStyle>;
};

Countdown.displayName = 'Countdown';

export default Countdown;
